/*
 * Orvian Forms — lo que comparten el editor, la página para responder y la pestaña de respuestas.
 *
 * Contrato completo (modelo, formatos de respuesta, API): email/claude/formularios.md.
 * La validación y el recorrido de secciones son ESPEJO de backend/formularios.py: el servidor es
 * la autoridad (vuelve a validar todo); aquí solo sirven para avisar antes de enviar.
 */
import DOMPurify from 'dompurify';

/* ============================== Tipos ============================== */

// `grupo` ordena el selector de tipo como Google Forms (con separadores entre grupos).
// `icono` es el nombre del ícono de lucide-react que usa el portal.
export const TIPOS = {
  texto_corto: { nombre: 'Respuesta corta', icono: 'AlignLeft', grupo: 1, responde: true },
  parrafo: { nombre: 'Párrafo', icono: 'AlignJustify', grupo: 1, responde: true },
  opcion_multiple: { nombre: 'Opción múltiple', icono: 'CircleDot', grupo: 2, responde: true, eleccion: true },
  casillas: { nombre: 'Casillas', icono: 'SquareCheck', grupo: 2, responde: true, eleccion: true },
  desplegable: { nombre: 'Desplegable', icono: 'ChevronDownCircle', grupo: 2, responde: true, eleccion: true },
  archivo: { nombre: 'Subir archivos', icono: 'CloudUpload', grupo: 3, responde: true },
  escala: { nombre: 'Escala lineal', icono: 'SlidersHorizontal', grupo: 4, responde: true },
  calificacion: { nombre: 'Calificación', icono: 'Star', grupo: 4, responde: true },
  cuadricula_opcion: { nombre: 'Cuadrícula de opción múltiple', icono: 'Grid3x3', grupo: 4, responde: true, cuadricula: true },
  cuadricula_casillas: { nombre: 'Cuadrícula de casillas', icono: 'LayoutGrid', grupo: 4, responde: true, cuadricula: true },
  fecha: { nombre: 'Fecha', icono: 'CalendarDays', grupo: 5, responde: true },
  hora: { nombre: 'Hora', icono: 'Clock', grupo: 5, responde: true },
  // Sin respuesta: se añaden desde la barra lateral del editor, no desde el selector de tipo
  titulo: { nombre: 'Título y descripción', icono: 'Heading', grupo: 9, responde: false },
  imagen: { nombre: 'Imagen', icono: 'Image', grupo: 9, responde: false },
  video: { nombre: 'Vídeo', icono: 'Youtube', grupo: 9, responde: false },
  seccion: { nombre: 'Sección', icono: 'Rows3', grupo: 9, responde: false },
};

export const TIPOS_PREGUNTA = Object.keys(TIPOS).filter((t) => TIPOS[t].responde);
export const esPregunta = (item) => !!TIPOS[item?.tipo]?.responde;
export const esEleccion = (item) => !!TIPOS[item?.tipo]?.eleccion;
export const esCuadricula = (item) => !!TIPOS[item?.tipo]?.cuadricula;

/** Tipos que se califican solos en un cuestionario (el resto, a mano). */
export const AUTOCALIFICABLES = ['opcion_multiple', 'casillas', 'desplegable', 'texto_corto', 'cuadricula_opcion', 'cuadricula_casillas'];

export const INICIO = '__inicio__';
export const OTRO = '__otro__';

export const TIPOS_ARCHIVO = {
  documento: { nombre: 'Documento', ext: '.doc,.docx,.odt,.rtf,.txt' },
  hoja: { nombre: 'Hoja de cálculo', ext: '.xls,.xlsx,.ods,.csv' },
  presentacion: { nombre: 'Presentación', ext: '.ppt,.pptx,.odp' },
  pdf: { nombre: 'PDF', ext: '.pdf' },
  imagen: { nombre: 'Imagen', ext: 'image/*' },
  video: { nombre: 'Vídeo', ext: 'video/*' },
  audio: { nombre: 'Audio', ext: 'audio/*' },
  dibujo: { nombre: 'Dibujo', ext: '.svg,.vsdx,.drawio' },
};
export const MAX_ARCHIVOS = [1, 5, 10];
export const MAX_MB = [1, 10, 100, 500];

/** `accept` del <input type=file> para los tipos elegidos (vacío = cualquiera). */
export const acceptDeArchivo = (tipos) => (tipos || []).map((t) => TIPOS_ARCHIVO[t]?.ext).filter(Boolean).join(',');

export const ICONOS_CALIFICACION = { estrella: 'Estrella', corazon: 'Corazón', pulgar: 'Pulgar arriba' };

/* ============================== Validación (catálogo para la interfaz) ============================== */

export const VALIDACIONES = {
  numero: {
    nombre: 'Número',
    ops: {
      mayor: 'Mayor que', mayor_igual: 'Mayor o igual que', menor: 'Menor que', menor_igual: 'Menor o igual que',
      igual: 'Igual a', distinto: 'Distinto de', entre: 'Entre', no_entre: 'No está entre', es_numero: 'Es un número', entero: 'Número entero',
    },
  },
  texto: { nombre: 'Texto', ops: { contiene: 'Contiene', no_contiene: 'No contiene', correo: 'Correo electrónico', url: 'URL' } },
  longitud: { nombre: 'Longitud', ops: { max: 'Número máximo de caracteres', min: 'Número mínimo de caracteres' } },
  regex: { nombre: 'Expresión regular', ops: { contiene: 'Contiene', no_contiene: 'No contiene', coincide: 'Coincide con', no_coincide: 'No coincide con' } },
  seleccion: { nombre: 'Selección', ops: { al_menos: 'Selecciona al menos', como_maximo: 'Selecciona como máximo', exactamente: 'Selecciona exactamente' } },
};

/** Qué validaciones admite cada tipo. */
export function validacionesDe(tipo) {
  if (tipo === 'texto_corto') return ['numero', 'texto', 'longitud', 'regex'];
  if (tipo === 'parrafo') return ['longitud', 'regex'];
  if (tipo === 'casillas') return ['seleccion'];
  return [];
}

const OPS_SIN_VALOR = new Set(['es_numero', 'entero', 'correo', 'url']);
export const opNecesitaValor = (op) => !OPS_SIN_VALOR.has(op);
export const opNecesitaDosValores = (op) => op === 'entre' || op === 'no_entre';

/* ============================== Ids y valores por defecto ============================== */

export function nuevoId() {
  try {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 10);
  } catch {
    return Math.random().toString(36).slice(2, 12).padEnd(10, '0');
  }
}

const opcion = (texto) => ({ id: nuevoId(), texto, imagen: null, ir_a: 'siguiente' });

/** Item nuevo de un tipo, con lo mínimo para que se vea como en Google Forms. */
export function nuevoItem(tipo, { obligatoria = false, puntos = 0 } = {}) {
  const base = { id: nuevoId(), tipo, titulo: '', descripcion: '', imagen: null };
  const pregunta = { ...base, obligatoria, puntos, clave: null, retro: null };
  switch (tipo) {
    case 'texto_corto':
    case 'parrafo':
      return { ...pregunta, validacion: null };
    case 'opcion_multiple':
    case 'desplegable':
      return { ...pregunta, opciones: [opcion('Opción 1')], otra: false, mezclar_opciones: false, ir_segun_respuesta: false };
    case 'casillas':
      return { ...pregunta, opciones: [opcion('Opción 1')], otra: false, mezclar_opciones: false, validacion: null };
    case 'archivo':
      return { ...pregunta, archivo: { tipos: [], max_archivos: 1, max_mb: 10 } };
    case 'escala':
      return { ...pregunta, escala: { min: 1, max: 5, etiqueta_min: '', etiqueta_max: '' } };
    case 'calificacion':
      return { ...pregunta, calificacion: { max: 5, icono: 'estrella' } };
    case 'cuadricula_opcion':
    case 'cuadricula_casillas':
      return {
        ...pregunta,
        filas: [{ id: nuevoId(), texto: 'Fila 1' }],
        columnas: [{ id: nuevoId(), texto: 'Columna 1' }],
        una_por_columna: false,
        mezclar_filas: false,
      };
    case 'fecha':
      return { ...pregunta, fecha: { incluir_anio: true, incluir_hora: false } };
    case 'hora':
      return { ...pregunta, hora: { duracion: false } };
    case 'video':
      return { ...base, video: { url: '', youtube_id: '' } };
    case 'seccion':
      return { ...base, titulo: 'Sección sin título', despues: 'siguiente' };
    default: // titulo, imagen
      return base;
  }
}

/** Cambiar el tipo de una pregunta conservando lo que se pueda (título, descripción, opciones…). */
export function cambiarTipo(item, tipo) {
  const nuevo = nuevoItem(tipo, { obligatoria: !!item.obligatoria, puntos: item.puntos || 0 });
  const conservado = { ...nuevo, id: item.id, titulo: item.titulo, descripcion: item.descripcion, imagen: item.imagen };
  if (TIPOS[tipo]?.eleccion && item.opciones?.length) {
    conservado.opciones = item.opciones.map((o) => ({ ...o, ir_a: o.ir_a || 'siguiente' }));
    if (tipo !== 'desplegable') conservado.otra = !!item.otra;
  }
  if (TIPOS[tipo]?.cuadricula && item.filas?.length) {
    conservado.filas = item.filas;
    conservado.columnas = item.columnas;
  }
  return conservado;
}

/** Duplicar con ids nuevos (también opciones, filas y columnas, o se mezclarían las respuestas). */
export function duplicarItem(item) {
  const copia = JSON.parse(JSON.stringify(item));
  copia.id = nuevoId();
  const mapa = {};
  ['opciones', 'filas', 'columnas'].forEach((lista) => {
    (copia[lista] || []).forEach((x) => {
      const nuevo = nuevoId();
      mapa[x.id] = nuevo;
      x.id = nuevo;
    });
  });
  const correctas = copia.clave?.correctas;
  if (Array.isArray(correctas)) copia.clave.correctas = correctas.map((c) => mapa[c] || c);
  else if (correctas && typeof correctas === 'object') {
    copia.clave.correctas = Object.fromEntries(
      Object.entries(correctas).map(([fila, cols]) => [mapa[fila] || fila, (cols || []).map((c) => mapa[c] || c)]),
    );
  }
  return copia;
}

/* ============================== Secciones y recorrido ============================== */

/** `[{id, item, items}]`: la primera sección es INICIO (item null, el encabezado del formulario). */
export function secciones(items) {
  const out = [{ id: INICIO, item: null, items: [] }];
  (items || []).forEach((it) => {
    if (it.tipo === 'seccion') out.push({ id: it.id, item: it, items: [] });
    else out[out.length - 1].items.push(it);
  });
  return out;
}

/** Destinos posibles para "ir a" (para los selectores del editor). */
export function destinos(items) {
  return secciones(items).map((s, i) => ({
    id: s.id,
    nombre: `Ir a la sección ${i + 1}${s.item?.titulo ? ` (${s.item.titulo})` : ''}`,
  }));
}

function destinoDe(seccion, indice, lista, form, respuestas) {
  let destino = null;
  seccion.items.forEach((it) => {
    if (!it.ir_segun_respuesta || !['opcion_multiple', 'desplegable'].includes(it.tipo)) return;
    const v = respuestas?.[it.id];
    if (!v || !v.opcion) return;
    const op = (it.opciones || []).find((o) => o.id === v.opcion);
    if (op) destino = op.ir_a || 'siguiente';
  });
  if (!destino) destino = seccion.item ? (seccion.item.despues || 'siguiente') : (form.despues_primera || 'siguiente');
  if (destino === 'siguiente') return indice + 1 < lista.length ? lista[indice + 1].id : 'enviar';
  if (destino === 'enviar') return 'enviar';
  return lista.some((s) => s.id === destino) ? destino : (indice + 1 < lista.length ? lista[indice + 1].id : 'enviar');
}

/**
 * Secciones que recorre quien responde con estas respuestas, en orden. Una sección ya visitada
 * termina el recorrido (no hay bucles). Igual que `recorrido()` de backend/formularios.py.
 */
export function recorrido(form, respuestas) {
  const lista = secciones(form?.items);
  const visitadas = [];
  let actual = INICIO;
  while (actual && actual !== 'enviar') {
    if (visitadas.includes(actual)) break;
    visitadas.push(actual);
    const i = lista.findIndex((s) => s.id === actual);
    if (i < 0) break;
    actual = destinoDe(lista[i], i, lista, form, respuestas);
  }
  return visitadas;
}

/** A dónde lleva terminar la sección `id` ("enviar" o el id de la siguiente). */
export function siguienteSeccion(form, id, respuestas) {
  const lista = secciones(form?.items);
  const i = lista.findIndex((s) => s.id === id);
  if (i < 0) return 'enviar';
  return destinoDe(lista[i], i, lista, form, respuestas);
}

/* ============================== Validar una respuesta ============================== */

export function vacia(item, valor) {
  if (valor === null || valor === undefined || valor === '') return true;
  switch (item.tipo) {
    case 'texto_corto':
    case 'parrafo':
      return !String(valor).trim();
    case 'opcion_multiple':
    case 'desplegable':
      return !(valor.opcion || (valor.otro !== undefined && valor.otro !== null && String(valor.otro).trim()));
    case 'casillas':
      return !((valor.opciones || []).length || (valor.otro && String(valor.otro).trim()));
    case 'cuadricula_opcion':
    case 'cuadricula_casillas':
      return !Object.values(valor || {}).some((v) => (Array.isArray(v) ? v.length : !!v));
    case 'archivo':
      return !Array.isArray(valor) || !valor.length;
    default:
      return false;
  }
}

const RE_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const RE_URL = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i;
const esNumero = (s) => /^\s*-?\d+(\.\d+)?\s*$/.test(String(s));

function regex(patron) {
  try {
    return new RegExp(patron);
  } catch {
    return null;
  }
}

/** Mensaje de error de una validación, o null si pasa. */
export function fallaValidacion(v, valor) {
  if (!v || !v.tipo) return null;
  const msg = (defecto) => v.mensaje || defecto;
  const a = Number(v.valor);
  const b = Number(v.valor2);
  if (v.tipo === 'numero') {
    const s = String(valor ?? '').trim();
    if (!esNumero(s)) return msg('Introduce un número');
    const n = Number(s);
    const ok = {
      mayor: n > a, mayor_igual: n >= a, menor: n < a, menor_igual: n <= a, igual: n === a, distinto: n !== a,
      entre: n >= Math.min(a, b) && n <= Math.max(a, b), no_entre: n < Math.min(a, b) || n > Math.max(a, b),
      es_numero: true, entero: Number.isInteger(n),
    }[v.op];
    if (ok === false) {
      const txt = VALIDACIONES.numero.ops[v.op] || '';
      return msg(v.op === 'entero' ? 'Introduce un número entero' : `El número debe ser: ${txt.toLowerCase()} ${v.valor ?? ''}${opNecesitaDosValores(v.op) ? ` y ${v.valor2}` : ''}`.trim());
    }
    return null;
  }
  if (v.tipo === 'texto') {
    const s = String(valor ?? '');
    if (v.op === 'contiene' && !s.includes(v.valor || '')) return msg(`Debe contener "${v.valor}"`);
    if (v.op === 'no_contiene' && v.valor && s.includes(v.valor)) return msg(`No puede contener "${v.valor}"`);
    if (v.op === 'correo' && !RE_CORREO.test(s.trim())) return msg('Introduce un correo electrónico válido');
    if (v.op === 'url' && !RE_URL.test(s.trim())) return msg('Introduce una URL válida');
    return null;
  }
  if (v.tipo === 'longitud') {
    const n = String(valor ?? '').length;
    if (v.op === 'max' && n > a) return msg(`Debe tener como máximo ${a} caracteres`);
    if (v.op === 'min' && n < a) return msg(`Debe tener al menos ${a} caracteres`);
    return null;
  }
  if (v.tipo === 'regex') {
    const re = regex(v.valor || '');
    if (!re) return null; // un patrón roto no bloquea a quien responde (el editor ya lo avisa)
    const s = String(valor ?? '');
    const contiene = re.test(s);
    const coincide = (() => { const m = s.match(re); return !!m && m[0] === s; })();
    const ok = { contiene, no_contiene: !contiene, coincide, no_coincide: !coincide }[v.op];
    return ok === false ? msg('La respuesta no tiene el formato esperado') : null;
  }
  if (v.tipo === 'seleccion') {
    const n = (valor?.opciones || []).length + (valor?.otro && String(valor.otro).trim() ? 1 : 0);
    if (v.op === 'al_menos' && n < a) return msg(`Selecciona al menos ${a} opciones`);
    if (v.op === 'como_maximo' && n > a) return msg(`Selecciona como máximo ${a} opciones`);
    if (v.op === 'exactamente' && n !== a) return msg(`Selecciona exactamente ${a} opciones`);
    return null;
  }
  return null;
}

/** Error de una pregunta con este valor (obligatoria, formato, validación), o null. */
export function errorDe(item, valor) {
  if (!esPregunta(item)) return null;
  if (vacia(item, valor)) return item.obligatoria ? 'Esta pregunta es obligatoria' : null;
  if (esCuadricula(item)) {
    const filas = item.filas || [];
    if (item.obligatoria && filas.some((f) => {
      const v = valor?.[f.id];
      return Array.isArray(v) ? !v.length : !v;
    })) return 'Esta pregunta necesita una respuesta por fila';
    if (item.una_por_columna) {
      const usadas = [];
      for (const v of Object.values(valor || {})) {
        for (const c of (Array.isArray(v) ? v : [v])) {
          if (!c) continue;
          if (usadas.includes(c)) return 'Selecciona como máximo una respuesta por columna';
          usadas.push(c);
        }
      }
    }
    return null;
  }
  if (item.tipo === 'texto_corto' || item.tipo === 'parrafo' || item.tipo === 'casillas') return fallaValidacion(item.validacion, valor);
  return null;
}

/** Errores de las preguntas de una lista de items: `{item_id: mensaje}`. */
export function erroresDe(items, respuestas) {
  const out = {};
  (items || []).forEach((it) => {
    const e = errorDe(it, respuestas?.[it.id]);
    if (e) out[it.id] = e;
  });
  return out;
}

/* ============================== Mostrar respuestas ============================== */

const opcionTexto = (item, id) => (item.opciones || []).find((o) => o.id === id)?.texto ?? '';

/** Texto legible de una respuesta (resúmenes, vista individual, exportar en el cliente). */
export function textoDeValor(item, valor) {
  if (valor === null || valor === undefined) return '';
  switch (item.tipo) {
    case 'opcion_multiple':
    case 'desplegable':
      return valor.opcion ? opcionTexto(item, valor.opcion) : (valor.otro ?? '');
    case 'casillas':
      return [...(valor.opciones || []).map((id) => opcionTexto(item, id)), ...(valor.otro ? [valor.otro] : [])].join(', ');
    case 'cuadricula_opcion':
    case 'cuadricula_casillas':
      return (item.filas || []).map((f) => {
        const v = valor[f.id];
        const cols = (Array.isArray(v) ? v : [v]).filter(Boolean)
          .map((c) => (item.columnas || []).find((x) => x.id === c)?.texto || '').join(', ');
        return cols ? `${f.texto}: ${cols}` : null;
      }).filter(Boolean).join(' · ');
    case 'archivo':
      return (valor || []).map((a) => a.nombre || a.id).join(', ');
    case 'fecha':
      return formatoFecha(valor);
    default:
      return String(valor);
  }
}

export function formatoFecha(valor) {
  const m = /^(\d{4}-|--)(\d{2})-(\d{2})(?:T(\d{2}:\d{2}))?$/.exec(String(valor || '')); // 2026-09-16 o --09-16
  if (!m) return String(valor || '');
  const [, conAnio, mes, dia, hora] = m;
  const anio = conAnio === '--' ? '--' : conAnio.slice(0, 4);
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${Number(dia)} ${meses[Number(mes) - 1] || mes}${anio !== '--' ? ` ${anio}` : ''}${hora ? `, ${hora}` : ''}`;
}

/* ============================== Texto enriquecido (descripciones) ============================== */

const ETIQUETAS = ['b', 'strong', 'i', 'em', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'div', 'span'];

export function htmlSeguro(html) {
  const limpio = DOMPurify.sanitize(html || '', { ALLOWED_TAGS: ETIQUETAS, ALLOWED_ATTR: ['href', 'target', 'rel'] });
  // Los enlaces siempre abren fuera y sin dar acceso a esta página
  return limpio.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer nofollow" ');
}

export const textoPlano = (html) => (html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li)>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();

/* ============================== Vídeo ============================== */

export function youtubeId(url) {
  const s = String(url || '').trim();
  const m = s.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/i);
  if (m) return m[1];
  return /^[\w-]{11}$/.test(s) ? s : '';
}

/* ============================== Tema ============================== */

export const FUENTES = ['Inter', 'Roboto', 'Lora', 'Merriweather', 'Playfair Display', 'Montserrat', 'Nunito', 'Oswald'];

export const TAMANOS = {
  encabezado: { min: 18, max: 36, defecto: 24 },
  pregunta: { min: 9, max: 24, defecto: 12 },
  texto: { min: 9, max: 18, defecto: 11 },
};

// Colores de tema (los de Google Forms, en el orden de su selector) + los de la marca Orvian
export const COLORES_TEMA = [
  '#db4437', '#673ab7', '#3f51b5', '#4285f4', '#03a9f4', '#00bcd4', '#ff5722', '#ff9800',
  '#009688', '#4caf50', '#607d8b', '#9e9e9e', '#5f51bb', '#2f9170', '#c4861f', '#cf5a67',
];

export const TEMA_DEFECTO = {
  color: '#673ab7',
  fondo: '#f0ebf8',
  imagen: null,
  fuentes: {
    encabezado: { familia: 'Inter', tamano: 24 },
    pregunta: { familia: 'Inter', tamano: 12 },
    texto: { familia: 'Inter', tamano: 11 },
  },
};

function hexARgb(hex) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return [103, 58, 183];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const rgbAHex = (r, g, b) => `#${[r, g, b].map((x) => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('')}`;

function mezclar(hex, conHex, peso) {
  const [r1, g1, b1] = hexARgb(hex);
  const [r2, g2, b2] = hexARgb(conHex);
  return rgbAHex(r1 * peso + r2 * (1 - peso), g1 * peso + g2 * (1 - peso), b1 * peso + b2 * (1 - peso));
}

/** Los cuatro fondos que Google ofrece para un color: tenue, medio, oscuro y gris. */
export function fondosDe(color) {
  return [mezclar(color, '#ffffff', 0.1), mezclar(color, '#ffffff', 0.22), mezclar(color, '#ffffff', 0.38), '#f6f6f6'];
}

/** ¿Texto blanco o negro sobre este color? */
export function textoSobre(hex) {
  const [r, g, b] = hexARgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 170 ? '#1a1b22' : '#ffffff';
}

// Cabeceras prediseñadas: no hay imágenes de stock, así que son patrones dibujados con CSS.
// Cada una recibe el color del tema para combinar siempre con él.
export const PRESETS_CABECERA = {
  aurora: { nombre: 'Aurora', css: (c) => `linear-gradient(120deg, ${mezclar(c, '#2f9a86', 0.55)} 0%, ${c} 55%, ${mezclar(c, '#a960d0', 0.5)} 100%)` },
  ondas: {
    nombre: 'Ondas',
    css: (c) => `radial-gradient(120% 90% at 10% 110%, ${mezclar(c, '#ffffff', 0.55)} 0 30%, transparent 31%), radial-gradient(90% 80% at 90% -10%, ${mezclar(c, '#000000', 0.7)} 0 35%, transparent 36%), ${c}`,
  },
  confeti: {
    nombre: 'Celebración',
    css: (c) => `radial-gradient(circle at 12% 30%, #ffd166 0 6px, transparent 7px), radial-gradient(circle at 30% 70%, #ef476f 0 5px, transparent 6px), radial-gradient(circle at 55% 25%, #06d6a0 0 7px, transparent 8px), radial-gradient(circle at 78% 60%, #ffffff 0 5px, transparent 6px), radial-gradient(circle at 90% 20%, #118ab2 0 6px, transparent 7px), ${c}`,
  },
  lineas: { nombre: 'Líneas', css: (c) => `repeating-linear-gradient(135deg, ${c} 0 18px, ${mezclar(c, '#ffffff', 0.85)} 18px 36px)` },
  puntos: { nombre: 'Puntos', css: (c) => `radial-gradient(${mezclar(c, '#ffffff', 0.55)} 2px, transparent 2.5px) 0 0 / 18px 18px, ${c}` },
  atardecer: { nombre: 'Atardecer', css: (c) => `linear-gradient(180deg, #ffb86b 0%, ${mezclar(c, '#ef476f', 0.4)} 55%, ${c} 100%)` },
  montanas: {
    nombre: 'Montañas',
    css: (c) => `linear-gradient(160deg, transparent 55%, ${mezclar(c, '#000000', 0.55)} 55.5%), linear-gradient(20deg, transparent 50%, ${mezclar(c, '#000000', 0.75)} 50.5%), linear-gradient(180deg, ${mezclar(c, '#ffffff', 0.35)}, ${c})`,
  },
  cuadricula: {
    nombre: 'Cuadrícula',
    css: (c) => `linear-gradient(${mezclar(c, '#ffffff', 0.75)} 1px, transparent 1px) 0 0 / 22px 22px, linear-gradient(90deg, ${mezclar(c, '#ffffff', 0.75)} 1px, transparent 1px) 0 0 / 22px 22px, ${c}`,
  },
};

/** URL pública de una imagen del formulario. `base` = API (p. ej. `${API}`); por defecto relativa. */
export const urlImagen = (id, base = '/api') => (id ? `${base}/forms/imagenes/${id}` : '');

/** Fondo CSS de la cabecera (`imagen` del tema), o null si no hay. */
export function fondoCabecera(tema, base = '/api') {
  const img = tema?.imagen;
  if (!img) return null;
  if (img.id) return `center / cover no-repeat url("${urlImagen(img.id, base)}")`;
  const preset = PRESETS_CABECERA[img.preset];
  return preset ? preset.css(tema.color || TEMA_DEFECTO.color) : null;
}

/** Variables CSS del lienzo del formulario (forms.css las usa todas). */
export function varsTema(tema, { oscuro = false } = {}) {
  const t = { ...TEMA_DEFECTO, ...(tema || {}), fuentes: { ...TEMA_DEFECTO.fuentes, ...(tema?.fuentes || {}) } };
  const f = (clave) => ({ ...TEMA_DEFECTO.fuentes[clave], ...(t.fuentes[clave] || {}) });
  const fam = (clave) => `"${f(clave).familia}", Inter, system-ui, sans-serif`;
  return {
    '--fm-color': t.color,
    '--fm-color-texto': textoSobre(t.color),
    '--fm-color-suave': oscuro ? mezclar(t.color, '#1b1e2e', 0.28) : mezclar(t.color, '#ffffff', 0.12),
    '--fm-color-hover': oscuro ? mezclar(t.color, '#1b1e2e', 0.18) : mezclar(t.color, '#ffffff', 0.07),
    // En oscuro el fondo del tema se oscurece (conserva el tono) para no deslumbrar
    '--fm-fondo': oscuro ? mezclar(t.fondo || t.color, '#0e1019', 0.1) : (t.fondo || fondosDe(t.color)[0]),
    '--fm-fuente-encabezado': fam('encabezado'),
    '--fm-fuente-pregunta': fam('pregunta'),
    '--fm-fuente-texto': fam('texto'),
    // Tamaños en pt como Google (1pt ≈ 1.333px)
    '--fm-tam-encabezado': `${Math.round(f('encabezado').tamano * 1.333)}px`,
    '--fm-tam-pregunta': `${Math.round(f('pregunta').tamano * 1.333)}px`,
    '--fm-tam-texto': `${Math.round(f('texto').tamano * 1.333)}px`,
  };
}

const fuentesCargadas = new Set(['Inter']);

/** Carga de Google Fonts las familias del tema que falten (una sola vez por familia). */
export function cargarFuentes(tema) {
  const familias = Object.values(tema?.fuentes || {}).map((f) => f?.familia).filter((x) => x && FUENTES.includes(x));
  familias.forEach((familia) => {
    if (fuentesCargadas.has(familia)) return;
    fuentesCargadas.add(familia);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(familia).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  });
}

/* ============================== Utilidades ============================== */

/** Baraja estable por semilla (el orden no cambia en cada render de quien responde). */
export function barajar(lista, semilla) {
  const out = [...(lista || [])];
  let s = 0;
  for (const ch of String(semilla || 'x')) s = (s * 31 + ch.charCodeAt(0)) >>> 0;
  for (let i = out.length - 1; i > 0; i -= 1) {
    s = (s * 1103515245 + 12345) >>> 0;
    const j = s % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function tamanoLegible(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Puntos máximos de un item en un cuestionario (las cuadrículas puntúan por fila). */
export const maximoDe = (item) => (esCuadricula(item) ? (item.puntos || 0) * (item.filas || []).length : (item.puntos || 0));

export const puntosTotales = (items) => (items || []).filter(esPregunta).reduce((s, it) => s + maximoDe(it), 0);
